import { getENV, useCustomAddressStore, useDecisions } from '@libs/common-next';
import type { Country } from '@libs/core';

import { useProduct } from '@/entities/product';
import type { FormFields } from '@/entities/purchase-form';

import { getAutoshipData, getBopisData, getSddData, getSthData } from '../lib';

export const useNormalizeData = () => {
    const { product, tailored } = useProduct();
    const sddAddress = useCustomAddressStore(state => state.address);
    const autoshipFlag = useDecisions('ff_autoship');

    const country = getENV('NEXT_PUBLIC_LOCALE').split('-')[1] as Country; // US | CA - Hack because of Country Enum

    const tailoredBopisStoreId = tailored?.variants?.[product?.sku ?? '']?.BOPIS?.storeId;
    const tailoredSddStoreId = tailored?.variants?.[product?.sku ?? '']?.SDD?.storeId;

    const storeIds: Record<FormFields['fulfilmentMethod'], string> = {
        bopis: tailoredBopisStoreId || '',
        sdd: tailoredSddStoreId || '',
        sth: '', // No need to pass store ID for STH
    };

    return (formData: FormFields) => {
        if (!product) return null;

        const storeId = storeIds[formData.fulfilmentMethod].padStart(4, '0');

        const isAutoshipSelected = formData.purchaseOption === 'autoship';
        const isPrecrawl = autoshipFlag === 'precrawl';

        const baseData = {
            country,
            sku: product.sku,
            quantity: formData.quantity,
            fulfillmentMethod: isPrecrawl && isAutoshipSelected ? 'autoship' : formData.fulfilmentMethod,
        };

        const deliveryMethod = {
            sth: getSthData({
                formDataPersonalization: formData.personalization,
                productDataPersonalization: product.personalization,
                purchaseOption: formData.purchaseOption,
            }),
            bopis: getBopisData({ storeId }),
            sdd: getSddData({
                country,
                storeId,
                city: sddAddress?.city ?? '',
                stateProvince: sddAddress?.stateProvince ?? '',
                addressLine1: sddAddress?.streetLine1 ?? '',
                addressLine2: sddAddress?.streetLine2,
                postalCode: sddAddress?.zipPostalCode ?? '',
            }),
        };

        const deliveryMethodData = deliveryMethod[formData.fulfilmentMethod];

        return {
            ...baseData,
            ...deliveryMethodData,
            ...(isAutoshipSelected
                ? getAutoshipData({ frequency: formData.autoshipFrequency, unit: formData.autoshipUnit })
                : {}),
        };
    };
};
